import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import LayoutFixHome from "../components/LayoutFixHome";
import ProcessPerClientTable from "../components/ProcessesPerClientTable";
import ProcessPerClientCardView from "../components/ProcessPerClientCardView";
import ExpandCircleDownOutlinedIcon from "@mui/icons-material/ExpandCircleDownOutlined";
import { useNavigate } from "react-router-dom";
import { RoutesConfig } from "../router/types";
import VisualizacaoToggle from "../components/VisualizacaoToggle";
import { toast } from "react-toastify";
import { getAllProcessesByEmail } from "../services/clientService";
import { useUserStore } from "../store/userStore";

interface ProcessPerClientProps {}

const MyProcessesPage: React.FC<ProcessPerClientProps> = (
  props: ProcessPerClientProps
) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [hasMore, setHasMore] = useState(true);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [visualizacao, setVisualizacao] = useState<"card" | "list">(
    isMobile ? "card" : "list"
  );
  const currentUserEmail = useUserStore((state) => state.email);
  const [resultAllProcesses, setResultAllProcesses] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(false);
  const profile = useUserStore((state) => state.profile);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  const handleBack = () => {
    navigate(RoutesConfig.HOME.path);
  };

  const getAllProcessesFunc = async () => {
    if (!currentUserEmail || isLoading || !hasMore) return;

    setIsLoading(true);
    try {
      const data = await getAllProcessesByEmail(
        currentUserEmail,
        nextCursor,
        10
      );

      setResultAllProcesses((prevProcesses: any) => {
        const newClientProcesses = data.items.filter(
          (newClient: any) =>
            !prevProcesses.some(
              (existing: any) => existing.CD_PROCESSO === newClient.CD_PROCESSO
            )
        );

        return [...prevProcesses, ...newClientProcesses];
      });

      setNextCursor(data.nextCursor);
      setHasMore(data.hasNextPage);
    } catch (err) {
      toast.error("Erro ao buscar processos associados");
      console.error("Erro ao buscar processos associados:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isFirstLoad && resultAllProcesses) {
      setIsFirstLoad(false);
    }
  }, [resultAllProcesses]);

  useEffect(() => {
    if (currentUserEmail) {
      getAllProcessesFunc();
    }
  }, [currentUserEmail]);

  const handleLoadMore = () => {
    if (hasMore && nextCursor) {
      getAllProcessesFunc();
    }
  };

  const reloadClientProcesses = async () => {
    setResultAllProcesses([]);
    setNextCursor(null);
    setHasMore(true);
    setIsLoading(true);
    try {
      const data = await getAllProcessesByEmail(currentUserEmail, null, 10);

      if (data?.items?.length) {
        setResultAllProcesses(data.items);
        setNextCursor(data.nextCursor);
        setHasMore(data.hasNextPage);
      } else {
        setResultAllProcesses([]);
        setHasMore(false);
      }
    } catch (err) {
      toast.error("Erro ao recarregar processos");
      console.error("Erro ao recarregar processos:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const autoLoadCount = useRef(0);

  useEffect(() => {
    const checkContentHeight = () => {
      const contentHeight = document.body.scrollHeight;
      const windowHeight = window.innerHeight;

      if (
        hasMore &&
        contentHeight <= windowHeight &&
        autoLoadCount.current < 4
      ) {
        autoLoadCount.current += 1;
        handleLoadMore();
      }
    };

    const timeout = setTimeout(checkContentHeight, 200);
    return () => clearTimeout(timeout);
  }, [resultAllProcesses.length, hasMore]);

  return (
    <>
      <LayoutFixHome>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            marginBottom: "29px",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 1,
              mt: {
                sm: "75px",
                xs: "75px",
                md: 0,
              },
            }}
          >
            <Box
              onClick={handleBack}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <ExpandCircleDownOutlinedIcon
                sx={{
                  transform: "rotate(-270deg)",
                  color: "#0A0C33",
                  height: "30px",
                  width: "30px",
                }}
              />
            </Box>

            <Typography
              variant="h5"
              sx={{ fontWeight: "bold", color: "#0A0C33" }}
            >
              Meus Processos
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mt: {
                sm: "75px",
                xs: "75px",
                md: 0,
              },
            }}
          >
            {!isMobile && <VisualizacaoToggle onChange={setVisualizacao} />}
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            height: "100%",
          }}
        >
          {visualizacao === "card" ? (
            <ProcessPerClientCardView
              list={resultAllProcesses}
              loadMoreData={handleLoadMore}
              isLoading={isLoading}
              reloadData={reloadClientProcesses}
            />
          ) : (
            <ProcessPerClientTable
              list={resultAllProcesses}
              height="79vh"
              backgroundColor="#A5EB17"
              textColorChange
              loadMoreData={handleLoadMore}
              isLoading={isLoading}
              reloadData={reloadClientProcesses}
            />
          )}
          {isLoading && resultAllProcesses.length === 0 && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              padding={4}
            >
              <CircularProgress
                size={48}
                thickness={4}
                style={{ color: "#326AFD" }}
              />
            </Box>
          )}
        </Box>
      </LayoutFixHome>
    </>
  );
};

export default MyProcessesPage;
